import type { MiddlewareAPI } from 'redux';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/operator/takeUntil';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';
import type { ActionsObservable } from 'redux-observable';
import { Observable } from 'rxjs/Observable';
import {
	toErrorId,
	toErrorMessage,
} from '@atlassian/jira-project-settings-apps-common/src/types.tsx';
import { famaCatchAndLogFetchError } from '@atlassian/jira-project-settings-apps-common/src/util/catch-and-log-fetch-error.tsx';
import { PACKAGE_NAME } from '../constants.tsx';
import {
	type Action,
	GET_COMPONENTS_PAGE_REQUEST,
	type GetComponentsPageRequestAction,
	setComponentsPage,
} from '../state/actions.tsx';
import { getBaseUrl } from '../state/context/selectors.tsx';
import { getProjectKey } from '../state/project/selectors.tsx';
import { getFilter, getOrderBy } from '../state/query/selectors.tsx';
import type { State } from '../state/reducer.tsx';
import { requestComponents } from '../state/requests/retrieve.tsx';
import { set as setError } from '../state/ui/error/actions.tsx';
import { getMaxResults, getStartAt } from '../state/ui/page/selectors.tsx';

export const retrieveComponents = (
	action$: ActionsObservable<Action>,
	store: MiddlewareAPI<State>,
): Observable<Action> => {
	const getComponentsRequestAction$ = action$.ofType(GET_COMPONENTS_PAGE_REQUEST);

	return getComponentsRequestAction$.mergeMap(({ payload }: GetComponentsPageRequestAction) => {
		const state = store.getState();
		const page = payload.page;
		const baseUrl = getBaseUrl(state);
		const projectKey = getProjectKey(state);
		const startAt = getStartAt(state, page);
		const maxResults = getMaxResults(state);
		const orderBy = getOrderBy(state);
		const filter = getFilter(state);

		return Observable.fromPromise(
			famaCatchAndLogFetchError(
				requestComponents(baseUrl, projectKey, startAt, maxResults, orderBy, filter),
				PACKAGE_NAME,
				toErrorId('retrieveComponents'),
				toErrorMessage('Error fetching components'),
			),
		)
			.mergeMap((result) =>
				// @ts-expect-error - TS2769 - No overload matches this call.
				Observable.of(setComponentsPage(result), setError({ message: '' })),
			)
			.takeUntil(getComponentsRequestAction$)
			.catch(() =>
				/*
				 * We are not logging the error here because it was already logged inside famaCatchAndLogFetchError call.
				 */
				Observable.of(setError({ message: 'Error retrieving components' })),
			);
	});
};
